import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";

import CustomActionMenu from "@components/CustomActionMenu/CustomActionMenu";
import CustomHeader from "@components/CustomHeader/CustomHeader";
import CustomTable from "@components/CustomTable/CustomTable";
import Loader from "@components/Loader/Loader";
import { CreateModalContext } from "layouts/appLayout/layout";
import { useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";

import CreateProjectComponent from "../CreateProjectComponent/CreateProjectComponent";
import { useGetProjectsByCompanyQuery } from "store/project/project.api";

const ProjectsPageAdmin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);

  const [openMenuId, setOpenMenuId] = useState(null);
  const { toggleCreateModal } = useContext(CreateModalContext);
  const { data: projects, isLoading } = useGetProjectsByCompanyQuery(
    currentUser.companyId._id
  );

  const [searchText, setSearchText] = useState("");

  // ✅ Permissions
  const canEditProject =
    currentUser?.roleId?.permissions?.includes("EDIT_PROJECT") || false;
  const showActionColumn = canEditProject;

  // ✅ Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;

  const headers = [
    { key: "title", label: t("projects.form.title") },
    { key: "description", label: t("projects.form.description") },
    { key: "startDate", label: t("projects.form.startDate") },
    { key: "endDate", label: t("projects.form.endDate") },
    { key: "supplier", label: t("projects.form.supplier") },
    { key: "collaborator", label: t("projects.form.collaborator") },
    { key: "createdAt", label: t("projects.table.createdAt") },
  ];

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString() : "-";

  const renderCell = (key, project) => {
    switch (key) {
      case "startDate":
      case "endDate":
      case "createdAt":
        return formatDate(project[key]);
      case "supplier":
        return project?.supplier?.name || "-";
      case "collaborator":
        return project?.collaborator?.name || "-";
      default:
        return project[key] || "-";
    }
  };

  const handleEditProject = (project) => {
    if (!canEditProject) return;
    setOpenMenuId(null);
    toggleCreateModal(
      <CreateProjectComponent
        onClose={() => toggleCreateModal(<></>)}
        project={project}
        isUpdate={true}
      />,
      t("projects.form.title_edit")
    );
  };

  const actionMenu = showActionColumn
    ? (project) => (
        <CustomActionMenu
          openMenuId={openMenuId}
          setOpenMenuId={setOpenMenuId}
          data={project}
          actions={[
            {
              label: t("projects.actions.edit"),
              onClick: () => handleEditProject(project),
            },
          ]}
        />
      )
    : undefined;

  const onSearchProject = (text) => setSearchText(text.toLowerCase());

  const filteredProjects = useMemo(() => {
    if (!projects?.data) return [];
    if (searchText.length <= 1) return projects.data;

    return projects.data.filter((project) => {
      const title = project.title?.toLowerCase() || "";
      const description = project.description?.toLowerCase() || "";
      return title.includes(searchText) || description.includes(searchText);
    });
  }, [projects?.data, searchText]);

  // ✅ Pagination logic
  const pageCount = Math.ceil(filteredProjects.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = currentPage * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredProjects.slice(start, end);
  }, [filteredProjects, currentPage]);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  if (isLoading) return <Loader />;

  return (
    <div className="p-5" style={{ overflowX: "auto" }}>
      <CustomHeader
        title={t("projects.header_title")}
        dataLength={filteredProjects.length}
        onSearch={onSearchProject}
      />

      <CustomTable
        headers={headers}
        data={paginatedData}
        rowKey="_id"
        renderCell={renderCell}
        actionMenu={actionMenu}
      />

      {/* ✅ Pagination en bas */}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
        <ReactPaginate
          previousLabel={"←"}
          nextLabel={"→"}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={1}
          pageRangeDisplayed={2}
          onPageChange={handlePageClick}
          containerClassName={"pagination"}
          activeClassName={"active"}
          forcePage={currentPage}
        />
      </div>
    </div>
  );
};

export default ProjectsPageAdmin;
